import React from "react";
import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Button, Modal, Form, Dropdown } from "react-bootstrap"
import swal from "sweetalert"
import { useDispatch, useSelector } from "react-redux"
import {
  getLeadsAction,
  getTelecallerlistAction,
  assignLeadsAction,
  deleteLeadAction,
} from "../../../../store/actions/LeadAction"
import { sendFormLinkAction } from "../../../../store/actions/ReceptionistAction"
import Sharelink from "../../../../../src/assets/images/share-link.png"
import Loader from "../../Common/Loader.jsx"
import { Tooltip } from "react-tooltip";
import { formatName } from "../../../../utils/formatName.js"

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr || dateStr === "" || !dateStr.includes("-")) return "-"
  const [yyyy, mm, dd] = dateStr.split("-")
  return `${dd}-${mm}-${yyyy}`
}

const Leads = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Redux state - properly accessing leadState
  const leadState = useSelector((state) => state.leadState || {})
  const { leads: reduxLeads = [], telecallers = [], loading = false, error = null, num_rows = 0 } = leadState

  const [isLoading, setIsLoading] = useState(false)
  const userDetails = JSON.parse(localStorage.getItem("userDetails"))
  const admin_id = userDetails?.admin_id || 0
  const roleName = userDetails?.role_name
  const roleDescription = userDetails?.role_modules?.role_description
  const isTelecaller = roleName?.toLowerCase() === "telecaller";
  // Local state
  const [leads, setLeads] = useState([])
  const [searchQueries, setSearchQueries] = useState({
    lead_id: "",
    lead_name: "",
    lead_email: "",
    lead_dob: "",
    lead_phone: "",
    diseases: "",
    telecaller_name: "",
  })

  // Sorting states
  const [sortField, setSortField] = useState("lead_id")
  const [sortOrder, setSortOrder] = useState("asc")

  // Pagination states - DYNAMIC
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")

  // Status filtering
  const [statusFilter, setStatusFilter] = useState("Not Converted")
  const [selectAll, setSelectAll] = useState(false)

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [searchTelecaller, setSearchTelecaller] = useState("")
  const [selectedTelecallerId, setSelectedTelecallerId] = useState(null)
  const [sendingLinkLeadId, setSendingLinkLeadId] = useState(null)

  // Handle patient click for navigation (used for spouse/partner)
  const handlePatientClick = (patientId) => {
    if (patientId) {
      console.log("Navigating to patient:", patientId)
      navigate(`/patients/patient-details/${patientId}`)
    }
  }

  // Fetch leads on component mount
  useEffect(() => {
    const fetchLeads = async () => {
      setIsLoading(true)
      try {
        await fetchLeadsFromServer()
      } catch (err) {
        console.error("Failed to fetch leads:", err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchLeads()
  }, [dispatch])

  // Update local state when redux state changes
  useEffect(() => {
    if (reduxLeads && reduxLeads.length > 0) {
      const formattedLeads = reduxLeads.map((lead) => {
        const safeDiseases = Array.isArray(lead.diseases) ? lead.diseases : [];
        const partner = lead.partnerDetails || null;
        
        // Format partner diseases if available
        let spouseDiseases = "-";
        if (partner && partner.diseases && Array.isArray(partner.diseases)) {
          spouseDiseases = partner.diseases.map(d => d.disease_name).join(", ");
        }
        
        return {
          lead_id: lead.lead_id,
          lead_name: lead.full_name || lead.lead_name || "",
          lead_email: lead.email || lead.lead_email || "-",
          lead_city: lead.city || lead.lead_city || "-",
          lead_phone: lead.phone_no || lead.lead_phone || "",
          lead_type: lead.lead_type || "-",
          telecaller_name: lead.telecaller_name || "-",
          diseases: safeDiseases,
          lead_status: lead.lead_status || "",
          is_converted: lead.is_converted || "Not Converted",
          platform: lead.platform || "",
          created_time: lead.created_time || "",
          ad_name: lead.ad_name || "",
          questionnaire_filled: lead.questionnaire_filled || "No",
          patient_id: lead.patient_id || null,
          selected: false,
          partnerInfo: partner
            ? {
                spouse_lead_id: partner.patient_id, // This is the partner's patient_id
                spouse_name: [partner.first_name, partner.middile_name, partner.last_name]
                  .filter(Boolean)
                  .join(" "),
                spouse_email: partner.patient_email || "-",
                spouse_phone: partner.whatsapp_contact_no || "-",
                spouse_city: partner.city || "-",
                spouse_priority: partner.lead_type || "-",
                spouse_disease: spouseDiseases,
                questionnaire_filled: partner.questionnaire_filled || "No",
                partner_added: partner.partner_added || "No"
              }
            : null
        };
      });

      // Filter by conversion status
      const filteredLeads =
        statusFilter === "All"
          ? formattedLeads
          : formattedLeads.filter((lead) => lead.is_converted === statusFilter);

      setLeads(filteredLeads);
    } else {
      setLeads([]);
    }
  }, [reduxLeads, statusFilter]);

  // DYNAMIC SERVER-SIDE FETCH FUNCTION
  const fetchLeadsFromServer = async (
    search_by = activeSearchField,
    search_keyword = activeSearchKeyword,
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = currentPage,
    status = statusFilter
  ) => {
    try {
      await dispatch(
        getLeadsAction(
          pagination,
          pageid,
          status,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      )
    } catch (err) {
      console.error("Failed to fetch Leads:", err)
    }
  }

  // DYNAMIC PAGE CHANGE HANDLER
  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchLeadsFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      page,
      statusFilter
    )
  }

  // Handle search with server-side filtering
  const handleSearch = (e, field) => {
    const keyword = e.target.value

    // Clear all search queries and set only the current field
    const clearedQueries = {
      lead_id: "",
      lead_name: "",
      lead_email: "",
      lead_city: "",
      lead_phone: "",
      lead_type: "",
      diseases: "",
      telecaller_name: ""
    }
    const updatedQueries = { ...clearedQueries, [field]: keyword }
    setSearchQueries(updatedQueries)
    setCurrentPage(0)

    const fieldToBackendColumnMap = {
      lead_id: "id",
      lead_name: "full_name",
      lead_email: "email",
      lead_city: "city",
      lead_type: "lead_type",
      lead_phone: "phone_no",
      diseases: "disease_name",
      telecaller_name: "telecaller_name"
    }

    const backendField = fieldToBackendColumnMap[field] || field
    setActiveSearchField(backendField)
    setActiveSearchKeyword(keyword)

    fetchLeadsFromServer(
      backendField,
      keyword,
      sortField,
      sortOrder,
      true,
      0,
      statusFilter
    )
  }

  // Handle sort with server-side sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    const fieldToBackendColumnMap = {
      lead_id: "id",
      lead_name: "full_name",
      lead_email: "email",
      lead_dob: "date_of_birth",
      lead_type: "lead_type",
      lead_phone: "phone_no",
      lead_city: "city",
      diseases: "disease_name",
      is_converted: "is_converted",
      telecaller_name: "telecaller_name",
    }

    const backendField = fieldToBackendColumnMap[field] || field
    setSortField(field)
    setSortOrder(order)

    fetchLeadsFromServer(
      activeSearchField,
      activeSearchKeyword,
      backendField,
      order,
      true,
      currentPage,
      statusFilter
    )
  }

  // Handle status filter change
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus)
    setCurrentPage(0)
    fetchLeadsFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      0,
      newStatus
    )
  }

  // Handle lead selection
  const handleSelectLead = (lead_id) => {
    const updatedLeads = leads.map((lead) =>
      lead.lead_id === lead_id ? { ...lead, selected: !lead.selected } : lead
    )
    setLeads(updatedLeads)

    // Check if all leads that match the current filter are selected
    const filteredLeads = updatedLeads.filter(
      (lead) => lead.is_converted === "Not Converted"
    )
    setSelectAll(filteredLeads.every((lead) => lead.selected))
  }

  // Handle select all
  const handleSelectAll = () => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)
    setLeads(leads.map((lead) => {
      if (lead.is_converted === "Not Converted") {
        return { ...lead, selected: newSelectAll }
      }
      return lead
    }))
  }

  // DYNAMIC PAGINATION CALCULATION
  const totalPages = Math.ceil(num_rows / rowsPerPage)

  // Handle edit
  const handleEdit = (lead) => {
    navigate(`/app-update-lead/${lead.lead_id}`, { state: { lead } })
  }

  // Handle view details - Navigates to Lead Details page
  const handleViewDetails = (lead) => {
    navigate(`/app-lead-details/${lead.lead_id}`, {
      state: {
        fromLeadsTable: true,
        questionnaireFilledStatus: lead.questionnaire_filled,
        isConverted: lead.is_converted,
        patient_id: lead.patient_id,
      },
    })
  }

  const handleDelete = (lead_id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this lead!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setIsLoading(true)
        dispatch(deleteLeadAction(lead_id))
          .then(() => {
            return fetchLeadsFromServer()
          })
          .catch((error) => {
            console.error("Delete failed:", error)
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    })
  }

  // Modal functions
  const handleShow = () => {
    dispatch(getTelecallerlistAction())
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
    setSearchTelecaller("")
    setSelectedTelecallerId(null)
  }

  // Filter telecallers
  const filteredTelecallers = Array.isArray(telecallers)
    ? telecallers.filter((caller) =>
        caller.name?.toLowerCase().includes(searchTelecaller.toLowerCase())
      )
    : []

  const handleSendLink = async (patientId) => {
    if (!patientId) return
    setSendingLinkLeadId(patientId)
    try {
      await dispatch(sendFormLinkAction(patientId))
      swal("Success", "Form link sent successfully!", "success")
    } catch (err) {
      swal("Error", "Failed to send form link", "error")
    } finally {
      setSendingLinkLeadId(null)
    }
  }

  // Handle assign
  const handleAssign = async () => {
    if (!selectedTelecallerId) {
      swal("Please select a telecaller to assign leads to", { icon: "error" });
      return;
    }
  
    const selectedLeads = leads
      .filter((lead) => lead.selected)
      .map((lead) => lead.lead_id);
  
    if (selectedLeads.length === 0) {
      swal("No leads selected to assign", { icon: "error" });
      return;
    }
  
    try {
      await dispatch(assignLeadsAction(selectedLeads, selectedTelecallerId));
  
      // Refresh list from server
      await fetchLeadsFromServer(
        activeSearchField,
        activeSearchKeyword,
        sortField,
        sortOrder,
        true,
        currentPage,
        statusFilter
      );
  
      // Reset selections
      setSelectAll(false);
  
      swal("Success", "Leads assigned successfully", "success");
      handleClose();
    } catch (err) {
      swal("Error", err.message || "Something went wrong while assigning leads", "error");
    }
  };
  
  // Check if any leads are selected
  const hasSelectedLeads = leads.some((lead) => lead.selected)

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      {/* Add Lead Button */}
      <div className="row mb-3">
      {!isTelecaller && (
  <div className="col-xl-12 text-end">
    <Link to="/app-add-lead" className="btn btn-primary btn-rounded">
      + Add Lead
    </Link>
  </div>
)}
      </div>

      {/* Table & Filters */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  {/* Filter & Assign Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-end align-items-md-center mb-3 gap-2">
                    {hasSelectedLeads && (
                      <Button onClick={handleShow} variant="outline-primary">
                        Assign To
                      </Button>
                    )}
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-primary" id="dropdown-status">
                        Status: {statusFilter}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleStatusFilterChange("All")}>
                          All Leads
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusFilterChange("Not Converted")}>
                          Not Converted
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusFilterChange("Converted")}>
                          Converted
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  {/* Assign Modal */}
                  <Modal show={showModal} onHide={handleClose}>
                    <Modal.Header closeButton>
                      <Modal.Title>Assign to Telecaller</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Search Telecaller</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Search by name"
                          value={searchTelecaller}
                          onChange={(e) => setSearchTelecaller(e.target.value)}
                        />
                      </Form.Group>
                      <div className="telecaller-list">
                        {filteredTelecallers?.length > 0 ? (
                          filteredTelecallers.map((caller) => (
                            <div key={caller.id} className="telecaller-item p-2">
                              <Form.Check
                                type="radio"
                                id={`telecaller-${caller.id}`}
                                name="telecaller"
                                label={
                                  <div>
                                    <div className="fw-bold">{caller.name}</div>
                                    <div className="text-muted small mt-1">{caller.contactNo}</div>
                                  </div>
                                }
                                checked={selectedTelecallerId === caller.id}
                                onChange={() => setSelectedTelecallerId(caller.id)}
                                className="d-flex align-items-start gap-2"
                              />
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-muted">No telecallers found</p>
                        )}
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant="secondary" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleAssign} disabled={!selectedTelecallerId}>
                        Assign
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  {/* Lead Table */}
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th>
                          {!isTelecaller && (
    <Form.Check type="checkbox" checked={selectAll} onChange={handleSelectAll} />
  )}
                          </th>
                          {roleName === "Admin" && (
                            <th onClick={() => handleSort("telecaller_name")} style={{ cursor: "pointer" }}>
                              Telecaller Name {sortField === "telecaller_name" && (sortOrder === "asc" ? "↑" : "↓")}
                              <input
                                className="form-control form-control-sm mt-1"
                                value={searchQueries.telecaller_name}
                                onChange={(e) => handleSearch(e, "telecaller_name")}
                                placeholder="Search Telecaller"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </th>
                          )}
                          <th onClick={() => handleSort("lead_name")} style={{ cursor: "pointer" }}>
                            Name {sortField === "lead_name" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.lead_name}
                              onChange={(e) => handleSearch(e, "lead_name")}
                              placeholder="Search Name"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("lead_email")} style={{ cursor: "pointer" }}>
                            Email {sortField === "lead_email" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.lead_email}
                              onChange={(e) => handleSearch(e, "lead_email")}
                              placeholder="Search Email"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("lead_phone")} style={{ cursor: "pointer" }}>
                            Phone {sortField === "lead_phone" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.lead_phone}
                              onChange={(e) => handleSearch(e, "lead_phone")}
                              placeholder="Search Phone"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("lead_city")} style={{ cursor: "pointer" }}>
                            City {sortField === "lead_city" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.lead_city}
                              onChange={(e) => handleSearch(e, "lead_city")}
                              placeholder="Search City"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("lead_type")} style={{ cursor: "pointer" }}>
                            Lead Type {sortField === "lead_type" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.lead_type}
                              onChange={(e) => handleSearch(e, "lead_type")}
                              placeholder="Search Priority"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("diseases")} style={{ cursor: "pointer" }}>
                            Diseases {sortField === "diseases" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.diseases}
                              onChange={(e) => handleSearch(e, "diseases")}
                              placeholder="Search Diseases"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("is_converted")} style={{ cursor: "pointer" }}>
                            Status {sortField === "is_converted" && (sortOrder === "asc" ? "↑" : "↓")}
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.length > 0 ? (
                          leads.map((lead) => (
                            <tr key={lead.lead_id} className="align-middle">
                              {/* Checkbox */}
                              <td>
                              {!isTelecaller && (
    lead.is_converted === "Not Converted" ? (
      <Form.Check
        type="checkbox"
        checked={lead.selected}
        onChange={() => handleSelectLead(lead.lead_id)}
      />
    ) : (
      <Form.Check type="checkbox" disabled />
    )
  )}
                              </td>

                              {/* Telecaller (Admin only) */}
                              {roleName === "Admin" && <td>{lead.telecaller_name || "-"}</td>}

                              {/* Lead + Spouse Name */}
                              <td>
                                <div style={{ fontSize: "15px" }}>
                                  <div>{formatName(lead.lead_name)}</div>
                                  {lead.partnerInfo?.spouse_name && (
                                    <div>{lead.partnerInfo.spouse_name}</div>
                                  )}
                                </div>
                              </td>

                              {/* Email */}
                              <td>
                                <div className="d-flex flex-column">
                                  <span style={{ fontSize: "14px" }}>{lead.lead_email}</span>
                                  {lead.partnerInfo && (
                                    <span className="mt-1" style={{ fontSize: "15px" }}>
                                      {lead.partnerInfo.spouse_email || "-"}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Phone */}
                              <td>
                                <div className="d-flex flex-column">
                                  <span style={{ fontSize: "14px" }}>{lead.lead_phone}</span>
                                  {lead.partnerInfo && (
                                    <span className="mt-1" style={{ fontSize: "15px" }}>
                                      {lead.partnerInfo.spouse_phone || "-"}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* City */}
                              <td>
                                <div className="d-flex flex-column">
                                  <span style={{ fontSize: "14px" }}>{lead.lead_city}</span>
                                  {lead.partnerInfo && (
                                    <span className="mt-1" style={{ fontSize: "15px" }}>
                                      {lead.partnerInfo.spouse_city || "-"}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Priority */}
                              <td>
                                <div className="d-flex flex-column">
                                  <span>{lead.lead_type}</span>
                                  {lead.partnerInfo && (
                                    <span className="mt-1" style={{ fontSize: "15px" }}>
                                      {lead.partnerInfo.spouse_priority || "-"}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Disease */}
                              <td>
                                <div className="d-flex flex-column">
                                  <span>
                                    {lead.diseases?.length > 0
                                      ? lead.diseases.map((d) => d.disease_name).join(", ")
                                      : "-"}
                                  </span>
                                  {lead.partnerInfo && (
                                    <span className="mt-1" style={{ fontSize: "15px" }}>
                                      {lead.partnerInfo.spouse_disease || "-"}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Converted Badge */}
                              <td>
                                <div className="d-flex flex-column align-items-start">
                                  <span
                                    className={`badge px-3 py-1 mb-1 rounded-pill text-white ${
                                      lead.is_converted === "Converted" ? "bg-success" : "bg-danger"
                                    }`}
                                    style={{ fontSize: "13px" }}
                                  >
                                    {lead.is_converted}
                                  </span>
                                
                                </div>
                              </td>

                              {/* Actions */}
                              <td>
                                <div className="d-flex flex-column gap-2 align-items-start">
                                  {/* MAIN LEAD ACTIONS */}
                                  <div className="d-flex gap-2 align-items-center">
                                    {/* 👁 View Icon for Main Lead - Navigates to Lead Details */}
                                    <Link
                                      to="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewDetails(lead); // Uses lead_id to show lead details
                                      }}
                                      title="View Lead Details"
                                      className="text-info"
                                    >
                                      <i className="fa fa-eye fs-5" />
                                    </Link>

                                    {/* Share Link Icon for Main Lead */}
                                    {lead.is_converted?.trim()?.toLowerCase() === "converted" &&
                                      lead.questionnaire_filled?.trim()?.toLowerCase() === "no" && (
                                        <>
                                          {sendingLinkLeadId === (lead.patient_id || lead.lead_id) ? (
                                            <div style={{ width: "24px", height: "24px" }}>
                                              <Loader size="sm" />
                                            </div>
                                          ) : (
                                            <>
                                              <img
                                                src={Sharelink || "/placeholder.svg"}
                                                alt="Send Link"
                                                className="ms-sm-2 mt-2 mt-sm-0"
                                                data-tooltip-id={`send-link-tooltip-${lead.lead_id}`}
                                                data-tooltip-content="This patient has not filled up the form. Click to share the form link."
                                                data-tooltip-place="top"
                                                style={{
                                                  cursor: "pointer",
                                                  width: "24px",
                                                  height: "24px",
                                                }}
                                                onClick={() =>
                                                  handleSendLink(lead.patient_id || lead.lead_id)
                                                }
                                              />
                                              <Tooltip
                                                id={`send-link-tooltip-${lead.lead_id}`}
                                                style={{
                                                  backgroundColor: "#000",
                                                  color: "#fff",
                                                  maxWidth: "220px",
                                                  textAlign: "center",
                                                }}
                                              />
                                            </>
                                          )}
                                        </>
                                      )}

                                    {/* Edit & Delete — only if not converted */}
                                    {lead.is_converted !== "Converted" && (
                                      <>
                                        <Link
                                          to="#"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleEdit(lead);
                                          }}
                                          className="text-success"
                                          title="Edit Lead"
                                        >
                                          <i className="fa fa-pencil-alt fs-5" />
                                        </Link>

                                        {!isTelecaller && (
  <span
    onClick={() => handleDelete(lead.lead_id)}
    className="text-danger"
    style={{ cursor: "pointer" }}
    title="Delete Lead"
  >
    <i className="fa fa-trash fs-5" />
  </span>
)}
                                      </>
                                    )}
                                  </div>

                                  {/* SPOUSE/PARTNER ACTIONS */}
                                  {lead.partnerInfo && (
                                    <div
                                      className="d-flex gap-2 align-items-center mt-1"
                                      style={{ fontSize: "15px" }}
                                    >
                                      {/* 👁 View Icon for Spouse - Navigates to Patient Details */}
                                      <Link
                                        to="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          // Navigate to patient details using spouse's patient_id
                                          if (lead.partnerInfo.spouse_lead_id) {
                                            handlePatientClick(lead.partnerInfo.spouse_lead_id);
                                          }
                                        }}
                                        title={`View ${lead.partnerInfo.spouse_name} Patient Details`}
                                        className="text-info"
                                      >
                                        <i className="fa fa-eye fs-5" />
                                      </Link>

                                      {/* Share Link Icon for Spouse */}
                                      {lead.partnerInfo?.questionnaire_filled?.trim()?.toLowerCase() === "no" && (
                                        <>
                                          {sendingLinkLeadId === lead.partnerInfo.spouse_lead_id ? (
                                            <div style={{ width: "24px", height: "24px" }}>
                                              <Loader size="sm" />
                                            </div>
                                          ) : (
                                            <>
                                              <img
                                                src={Sharelink || "/placeholder.svg"}
                                                alt="Send Link"
                                                className="ms-sm-2 mt-2 mt-sm-0"
                                                data-tooltip-id={`send-link-tooltip-spouse-${lead.partnerInfo.spouse_lead_id}`}
                                                data-tooltip-content={`${lead.partnerInfo.spouse_name} has not filled up the form. Click to share link.`}
                                                data-tooltip-place="top"
                                                style={{
                                                  cursor: "pointer",
                                                  width: "24px",
                                                  height: "24px",
                                                }}
                                                onClick={() =>
                                                  handleSendLink(lead.partnerInfo.spouse_lead_id)
                                                }
                                              />
                                              <Tooltip
                                                id={`send-link-tooltip-spouse-${lead.partnerInfo.spouse_lead_id}`}
                                                style={{
                                                  backgroundColor: "#000",
                                                  color: "#fff",
                                                  maxWidth: "220px",
                                                  textAlign: "center",
                                                }}
                                              />
                                            </>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={roleName === "Admin" ? "10" : "9"} className="text-center py-4">
                              No leads found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* DYNAMIC PAGINATION */}
                  {num_rows > 0 && (
                    <div
                      className="pagination-wrapper"
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "20px",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        Showing {leads?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                        {Math.min((currentPage + 1) * rowsPerPage, num_rows)} of {num_rows} entries
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                        <button
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                          disabled={currentPage === 0}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === 0 ? "not-allowed" : "pointer",
                            opacity: currentPage === 0 ? 0.5 : 1,
                          }}
                        >
                          &lt;
                        </button>
                        {(() => {
                          const pages = []
                          const maxPagesToShow = 5
                          const startPage = Math.max(
                            0,
                            Math.min(currentPage - 2, totalPages - maxPagesToShow)
                          )
                          const endPage = Math.min(startPage + maxPagesToShow, totalPages)

                          if (startPage > 0) {
                            pages.push(
                              <button
                                key={0}
                                onClick={() => handlePageChange(0)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                1
                              </button>
                            )
                            if (startPage > 1) {
                              pages.push(
                                <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                  ...
                                </span>
                              )
                            }
                          }

                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: isActive ? "#007bff" : "#fff",
                                  color: isActive ? "#fff" : "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {i + 1}
                              </button>
                            )
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                  ...
                                </span>
                              )
                            }
                            pages.push(
                              <button
                                key={totalPages - 1}
                                onClick={() => handlePageChange(totalPages - 1)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {totalPages}
                              </button>
                            )
                          }
                          return pages
                        })()}
                        <button
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                          disabled={currentPage === totalPages - 1}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                          }}
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Leads;